import { Matrix4, Object3D, Quaternion, Vector3 } from 'three';
import { CartesianCoordinates } from '../../util/Events/schema';

export const quaternionFromPositions = (from: Vector3, to: Vector3): Quaternion | undefined => {
  const targetIsTooClose = from.distanceTo(to) < 0.1;
  if (targetIsTooClose) return undefined;

  const mockObject = new Object3D();
  const dir = to.clone().sub(from).normalize();
  const beyondTo = to.clone().addScaledVector(dir, 3);
  while (from.distanceTo(beyondTo) < 2) {
    beyondTo.add(dir);
  }
  mockObject.position.copy(from);
  mockObject.lookAt(beyondTo);
  return mockObject.quaternion;
};

export const vector3ToCartesian = (vec: Vector3): CartesianCoordinates => {
  const [x, y, z] = vec.toArray();
  return { x, y, z };
};

// Retaining references to Vector3's prevent them from being garbage-collected,
// which can make the visualisation jerky if too much occurs at once
const vector3HoldingPen: Vector3[] = [];
export const temporaryVector3 = (x?: number, y?: number, z?: number) => {
  const v = vector3HoldingPen.pop() ?? new Vector3();
  v.set(x ?? 0, y ?? 0, z ?? 0);
  return v;
};
export const releaseVector3 = (...vectors: Vector3[]) => {
  vector3HoldingPen.push(...vectors);
};

export const horizontalQuaternion = new Quaternion().setFromRotationMatrix(
  new Matrix4().lookAt(new Vector3(1, 0, 0), new Vector3(0, 0, 0), new Vector3(0, 0, 1)),
);

export const distancePointToSegment = (
  p: Vector3,
  a: Vector3,
  b: Vector3,
  out?: Vector3,
): number => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const abz = b.z - a.z;

  const apx = p.x - a.x;
  const apy = p.y - a.y;
  const apz = p.z - a.z;

  const abLenSq = abx * abx + aby * aby + abz * abz;

  // Degenerate segment (A === B)
  if (abLenSq === 0) {
    out?.copy(a);
    return Math.sqrt(apx * apx + apy * apy + apz * apz);
  }

  // Project AP onto AB, clamped to [0, 1]
  let t = (apx * abx + apy * aby + apz * abz) / abLenSq;
  t = Math.max(0, Math.min(1, t));

  const closestX = a.x + abx * t;
  const closestY = a.y + aby * t;
  const closestZ = a.z + abz * t;

  const dx = p.x - closestX;
  const dy = p.y - closestY;
  const dz = p.z - closestZ;

  out?.set(closestX, closestY, closestZ);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};
